import { Banner } from 'component/banner';
import { FlowBox, HorizBox, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { colorSurfaceBannerInfo } from 'component/color';
import { useServersideConstructor } from 'component/constructor';
import { ConversationScreen } from 'component/screen';
import { Heading } from 'component/text';
import { questionTable } from 'feature/teaser/editorial-question';
import { topicTable } from 'feature/teaser/topic-photo-teaser';
import React from 'react';
import type { ArticleConfig } from 'structure/article';
import { TeaserScreen } from 'system/teaser';
import { useIsAdmin } from 'util/admin';
import { useCollection, useDatastore, useInstanceKey, useStructureKey } from 'util/datastore';
import { useConfig } from 'util/features';
import type { Feature } from 'util/stdtypes';
import { t } from 'util/stdtypes';

export const OnlyAdminButtonTeaserFeature: Feature<ArticleConfig, {}> = {
    name: 'Admin Teaser',
    key: 'teaserOnlyAdminButton',
    dev: false,
    subscreens: {
        adminLinks: OnlyAdminScreen,
    },
    providesConfig: {
        teaser: OnlyAdminTeaser,
    },
};

export function OnlyAdminTeaser() {
    const datastore = useDatastore();
    const instanceKey = useInstanceKey();
    const structureKey = useStructureKey();
    const isAdmin = useIsAdmin();

    const questions = useCollection(questionTable);
    const topics = useCollection(topicTable);
    if (!isAdmin) return null;

    const contentAssigned = questions.filter((q) => !q.deleted).length > 0 || topics.filter((t) => !t.deleted).length > 0;

    return (
        <TeaserScreen>
            <Banner color={colorSurfaceBannerInfo}>
                {!contentAssigned && <Heading label={t('No discussion assigned')} />}
                <HorizBox>
                    <CTAButton
                        label={t('Open admin screen')}
                        onPress={() =>
                            datastore.gotoInstanceScreen({
                                structureKey,
                                instanceKey,
                                screenKey: 'adminLinks',
                                params: {},
                            })
                        }
                    />
                </HorizBox>
            </Banner>
        </TeaserScreen>
    );
}

function OnlyAdminScreen() {
    return (
        <ConversationScreen>
            <AdminTeaserAction />
        </ConversationScreen>
    );
}

function AdminTeaserAction() {
    const instanceKey = useInstanceKey();
    const structureKey = useStructureKey();
    const datastore = useDatastore();

    const { additionalAdminButtons } = useConfig<ArticleConfig>();
    return (
        <PadBox top={10}>
            <FlowBox columnGap={10} rowGap={10}>
                <CTAButton
                    label={t('Create a topic')}
                    onPress={() =>
                        datastore.gotoInstanceScreen({ structureKey: 'admin', instanceKey: 'one', screenKey: 'createTopic', params: {} })
                    }
                    type='secondary'
                />
                <CTAButton
                    label={t('Assign thread')}
                    onPress={() => datastore.gotoInstanceScreen({ structureKey, instanceKey, screenKey: 'admin', params: {} })}
                    type='secondary'
                />
                <CTAButton
                    label={t('Configure Teaser Features (admin)')}
                    onPress={() => datastore.gotoInstanceScreen({ structureKey, instanceKey, screenKey: 'features', params: {} })}
                    type='secondary'
                />
                {additionalAdminButtons?.map((button, i) => (
                    <>{React.createElement(button, { key: 'adminbutton-' + i })}</>
                ))}
            </FlowBox>
        </PadBox>
    );
}
